import './styles/main.css';
import { h, replace } from './utils/dom.ts';
import { initLocale, onLocaleChange, t } from './i18n/index.ts';
import { interceptLinks, parseRoute, type TenantRoute } from './router.ts';
import { applyTheme, platformShell, tenantShell, type NavKey } from './components/layout.ts';
import { skeleton, toast } from './components/ui.ts';
import { loadTenant, publicRepository } from './services/public-api.ts';
import { AppError } from './services/errors.ts';
import { bookingPage } from './pages/booking.ts';
import { myBookingsPage } from './pages/my-bookings.ts';
import { homePage, notFoundPage } from './pages/home.ts';
import { legalPage } from './pages/legal.ts';
import { registerServiceWorker } from './sw-register.ts';
import type { PageContext } from './pages/context.ts';

/** Customer site (termin.quest). Contains no staff code; that is a separate app. */
const app = document.getElementById('app')!;
let disposers: (() => void)[] = [];
let renderId = 0;

function navigate(path: string): void {
  if (path !== location.pathname) history.pushState(null, '', path);
  void render();
}

async function render(): Promise<void> {
  const id = ++renderId;
  disposers.forEach((fn) => fn());
  disposers = [];
  const route = parseRoute(location.pathname);

  switch (route.name) {
    case 'home':
    case 'notFound':
    case 'platformLegal':
      applyTheme(null);
      initLocale('de');
      document.title = `termin.quest · ${t('app.title')}`;
      replace(app,
        route.name === 'home' ? homePage()
        : route.name === 'platformLegal' ? platformShell(legalPage(null, route.page))
        : notFoundPage());
      return;
    default:
      await renderTenant(route, id);
  }
}

async function renderTenant(route: TenantRoute, id: number): Promise<void> {
  replace(app, h('div', { class: 'shell' }, h('main', { class: 'content page' }, skeleton(4))));
  try {
    const business = await loadTenant(route.slug);
    if (id !== renderId) return; // user navigated away meanwhile
    initLocale(business.locale);
    applyTheme(business);
    const ctx: PageContext = { business, repo: publicRepository(business), navigate, onDispose: (fn) => disposers.push(fn) };
    const page: [NavKey | null, () => HTMLElement] =
      route.name === 'booking' ? ['book', () => bookingPage(ctx)]
      : route.name === 'my' ? ['my', () => myBookingsPage(ctx)]
      : [null, () => legalPage(business, route.page)];
    replace(app, tenantShell(business, page[0], page[1]()));
    window.scrollTo({ top: 0 });
  } catch (err) {
    if (id !== renderId) return;
    console.error(err);
    replace(app, notFoundPage(err instanceof AppError && err.code === 'not_found' ? 'error.tenantNotFound' : 'error.notFound'));
  }
}

window.addEventListener('popstate', () => void render());
interceptLinks(navigate);
onLocaleChange(() => void render());
window.addEventListener('offline', () => toast(t('app.offline'), 'info'));

void render();
registerServiceWorker();
